import { useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { api, formatDate } from "../api";
import { useApi } from "../hooks";
import { ErrorState, LoadingState, StatusBadge } from "./Ui";
import Dialog from "./Dialog";

// Jobs this deployment can run itself (api/cron/health, api/cron/syncs).
// Every job this deployment can run (routes/operations.js VERCEL_OWNABLE, utils/syncWorker.js ownedHere).
const VERCEL_OWNABLE = ["health", "google_ads", "ghl", "ctm", "brightlocal", "slack", "gmail", "analytics", "lsa", "keywords", "fulfillment"];
// Who runs a scheduled job (operational_schedules.owner).
const OWNER_LABEL: Record<string, string> = {
  vercel: "Runs on this Vercel deployment",
  external: "Runs on an external host",
  unverified: "Owner not verified",
};
// The state a sync run is in when nothing needs doing.
const HEALTHY_RUN = "Recent data";

type Schedule = {
  name: string;
  label: string;
  cadence: string;
  owner: string;
  owner_note: string | null;
  last_verified_at: string | null;
  verified_by_name: string | null;
};
type Run = {
  client_id: number;
  client_name: string;
  last_run: string;
  records: number;
  status: string;
  period_start: string | null;
  period_end: string | null;
  next_action: string;
};
type Readiness = {
  schedules: Schedule[];
  feeds: Array<{ id: string; label: string; state: string; runs: Run[] }>;
  clients: Array<{
    id: number;
    name: string;
    slug: string;
    primary_contact: string | null;
    primary_email: string | null;
    same_name_count: number;
  }>;
  overlaps: Array<{
    client_id: number;
    client_name: string;
    report_id: number;
    other_report_id: number;
    period_start: string;
    period_end: string;
    other_start: string;
    other_end: string;
  }>;
};
/** "Oct 2" this year, "Oct 2, 2025" otherwise: the dates in a sync history row stay on one line. */
const shortDay = (value: string | null | undefined) => {
  const full = formatDate(value);
  return full.endsWith(`, ${new Date().getFullYear()}`) ? full.replace(/, \d{4}$/, "") : full;
};

export function OperationalReadiness() {
  const request = useApi<Readiness>("/api/operations/readiness");
  const [editing, setEditing] = useState<Schedule | null>(null);
  const [owner, setOwner] = useState("unverified");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const edit = (s: Schedule) => {
    setEditing(s);
    setOwner(s.owner);
    setNote(s.owner_note || "");
    setError("");
  };
  const save = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await api(`/api/operations/schedules/${editing.name}`, {
        method: "PUT",
        body: JSON.stringify({ owner, owner_note: note }),
      });
      setEditing(null);
      request.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel settings-readiness">
      <div className="panel-heading">
        <div>
          <h2>Operational readiness</h2>
          <p>Data syncs, who runs each scheduled job, and client details still missing.</p>
        </div>
        <button className="icon-button" aria-label="Refresh readiness" onClick={request.reload}>
          <RefreshCw aria-hidden="true" />
        </button>
      </div>
      {request.error ? (
        <ErrorState title="Readiness is unavailable" message={request.error} onRetry={request.reload} />
      ) : !request.data ? (
        <LoadingState label="Loading readiness" />
      ) : (
        <>
          <details open>
            <summary>Provider sync history</summary>
            {request.data.feeds.map((feed) => (
              <section key={feed.id}>
                <h3>{feed.label}</h3>
                {feed.runs.length === 0 ? (
                  <p>
                    {feed.state}. Configure the provider, map the client and
                    verify a useful sync.
                  </p>
                ) : (
                  <div className="table-wrap sync-history">
                    <table>
                      <thead>
                        <tr>
                          <th>Client</th>
                          <th>Latest run</th>
                          <th>Reporting period</th>
                          <th className="num">Records</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feed.runs.map((run) => (
                          <tr key={run.client_id}>
                            <td>
                              <Link to={`/clients/${run.client_id}`}>
                                {run.client_name}
                              </Link>
                              {/* Phones: the run date and period sit under the name; their columns are hidden. */}
                              <small className="sync-history-when">
                                {shortDay(run.last_run)}{run.period_start ? `, for ${shortDay(run.period_start)} to ${shortDay(run.period_end)}` : ""}
                              </small>
                            </td>
                            <td className="sync-history-date">{shortDay(run.last_run)}</td>
                            <td className="sync-history-date">
                              {run.period_start
                                ? `${shortDay(run.period_start)} to ${shortDay(run.period_end)}`
                                : "Not recorded"}
                            </td>
                            <td className="num">{Number(run.records).toLocaleString()}</td>
                            <td>
                              {/* Quiet when the data is fine; a badge and the next step only when it is not. */}
                              {run.status === HEALTHY_RUN ? (
                                run.status
                              ) : (
                                <>
                                  <StatusBadge tone="warning">{run.status}</StatusBadge>
                                  <p className="muted">{run.next_action}</p>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}
          </details>
          <details>
            <summary>
              Scheduled job ownership ({request.data.schedules.length})
            </summary>
            <p className="muted">
              Recording an owner does not start a second scheduler.
              Health scoring, the Google Ads, GoHighLevel, CTM and BrightLocal syncs,
              and Slack and Gmail ingestion (tasks from messages) run here only
              while their owner is this Vercel deployment. Sends and paid scans
              stay on their existing host.
            </p>
            <ul className="module-list">
              {request.data.schedules.map((s) => (
                <li key={s.name}>
                  <strong>{s.label}</strong>
                  <span>
                    {s.cadence}. {OWNER_LABEL[s.owner] || s.owner}.
                  </span>
                  <p>{s.owner_note || "Nobody has recorded who runs this job."}</p>
                  {s.last_verified_at && (
                    <small>
                      Recorded {formatDate(s.last_verified_at)}
                      {s.verified_by_name ? ` by ${s.verified_by_name}` : ""}
                    </small>
                  )}
                  <button className="button secondary" onClick={() => edit(s)}>
                    Record owner
                  </button>
                </li>
              ))}
            </ul>
          </details>
          <details>
            <summary>
              Client input review ({request.data.clients.length})
            </summary>
            <ul className="module-list">
              {request.data.clients.map((c) => (
                <li key={c.id}>
                  <Link to={`/clients/${c.id}`}>{c.name}</Link>
                  <p>
                    {!c.primary_contact ? "Missing primary contact. " : ""}
                    {!c.primary_email ? "Missing email. " : ""}
                    {Number(c.same_name_count) > 1
                      ? "Same-name records exist; compare identities and histories before merging."
                      : ""}
                  </p>
                </li>
              ))}
            </ul>
          </details>
          <details>
            <summary>
              Overlapping report pairs ({request.data.overlaps.length}
              {request.data.overlaps.length === 100 ? "+" : ""})
            </summary>
            <p className="muted">
              Compare source coverage before choosing which totals to trust.
              Portal totals leave overlapping reports out; this list does not
              delete or merge anything.
            </p>
            <ul className="module-list">
              {request.data.overlaps.map((r) => (
                <li key={`${r.report_id}-${r.other_report_id}`}>
                  <Link
                    to={`/call-center?client_id=${r.client_id}&record=${r.report_id}`}
                  >
                    {r.client_name}: reports {r.report_id} and {r.other_report_id}
                  </Link>
                  <span>
                    {formatDate(r.period_start)} to {formatDate(r.period_end)}, and{" "}
                    {formatDate(r.other_start)} to {formatDate(r.other_end)}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        </>
      )}
      {editing && (
        <Dialog
          title={`Who runs ${editing.label}`}
          onClose={() => !busy && setEditing(null)}
        >
          <form className="operation-form"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <label>
              Owner
              <select value={owner} onChange={(e) => setOwner(e.target.value)}>
                <option value="unverified">Not verified</option>
                <option value="external">External host</option>
                {VERCEL_OWNABLE.includes(editing.name) && (
                  <option value="vercel">This Vercel deployment</option>
                )}
              </select>
            </label>
            <label>
              Where it runs and how you checked
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                minLength={10}
                maxLength={2000}
                required
              />
            </label>
            {error && <p role="alert">{error}</p>}
            <button className="button primary" disabled={busy}>
              {busy ? "Saving…" : "Save owner"}
            </button>
          </form>
        </Dialog>
      )}
    </section>
  );
}
